import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listInquiryDrafts, deleteInquiryDraftAdmin } from "@/lib/inquiry-draft.functions";
import { toast } from "sonner";
import { Loader2, Trash2, Pencil, FileEdit } from "lucide-react";
import { PageHeader } from "@/components/admin/shared";

export const Route = createFileRoute("/admin/drafts/")({
  component: DraftsListPage,
});

type Draft = {
  id: string;
  session_id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  event_date: string | null;
  location: string | null;
  guests: number | null;
  message: string | null;
  products: { slug: string; name: string; image: string; quantity?: number }[];
  created_at: string;
  updated_at: string;
};

function DraftsListPage() {
  const list = useServerFn(listInquiryDrafts);
  const del = useServerFn(deleteInquiryDraftAdmin);
  const [items, setItems] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      setItems((await list({})) as unknown as Draft[]);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const remove = async (id: string) => {
    if (!confirm("Ta bort utkast?")) return;
    try {
      await del({ data: { id } });
      toast.success("Borttaget");
      setItems((p) => p.filter((i) => i.id !== id));
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin" />;

  return (
    <>
      <PageHeader title={`Påbörjade förfrågningar (${items.length})`} />
      <p className="text-sm text-muted-foreground mb-4">
        Detta är formulär som besökare har börjat fylla i men <strong>inte skickat in</strong>.
        De räknas inte som inkomna förfrågningar.
      </p>

      {items.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <FileEdit className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Inga påbörjade förfrågningar.</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((i) => (
          <div
            key={i.id}
            className="rounded-xl border border-border bg-card p-4 flex items-center gap-3 flex-wrap"
          >
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
              Utkast
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">
                {i.name || <span className="text-muted-foreground italic">Inget namn</span>}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {i.email || "—"}
                {i.products.length > 0 && ` · ${i.products.length} produkter`}
                {i.message && " · har meddelande"}
              </div>
            </div>
            <div className="hidden sm:block text-xs text-muted-foreground whitespace-nowrap">
              {new Date(i.updated_at).toLocaleString("sv-SE")}
            </div>
            <Link
              to="/admin/drafts/$id"
              params={{ id: i.id }}
              className="p-2.5 rounded-md bg-secondary hover:bg-secondary/70 min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
              title="Öppna"
              aria-label="Öppna utkast"
            >
              <Pencil className="h-4 w-4" />
            </Link>
            <button
              onClick={() => remove(i.id)}
              className="p-2.5 rounded-md bg-destructive text-destructive-foreground min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
              aria-label="Ta bort utkast"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
